import { Component, OnInit, AfterViewInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { Chart, registerables } from 'chart.js';
import { forkJoin } from 'rxjs';
import { ShortageOverviewService } from './shortage-overview.service';
import { AppConstants } from '../../../app.constants';

Chart.register(...registerables);

@Component({
  selector: 'app-shortage-overview',
  templateUrl: './shortage-overview.component.html',
  styleUrl: './shortage-overview.component.scss'
})
export class ShortageOverviewComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() allData: any[] = [];
  @Input() currentMode: 'NDC' | 'Products' = 'NDC';
  @Output() filterRedirect = new EventEmitter<any>();

  // KPI Counts
  activeCount: number = 0;
  activeInShortagesCount: number = 0;
  discontinuedCount: number = 0;
  resolvedCount: number = 0;
  newThisMonthCount: number = 0;
  currentMonthYear: string = '';
  overviewIds: any = null;

  isLoading: boolean = false;
  analyticsData: any = null;

  // Charts
  charts: { [key: string]: any } = {};
  private refreshIntervalId: any = null;

  constructor(private overviewService: ShortageOverviewService) { }

  ngOnInit(): void {
    const date = new Date();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    this.currentMonthYear = `${month} ${year}`;

    this.fetchOverviewData();
    this.startAutoRefresh();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.renderCharts(), 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentMode'] && !changes['currentMode'].firstChange) {
      this.fetchOverviewData();
      this.startAutoRefresh();
    }
    if ((changes['allData'] && !changes['allData'].firstChange) ||
      (changes['currentMode'] && !changes['currentMode'].firstChange)) {
      this.renderCharts();
    }
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
    Object.values(this.charts).forEach(chart => chart.destroy());
  }

  startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.refreshIntervalId = setInterval(() => {
      this.fetchOverviewData(true);
    }, 10 * 60 * 1000);
  }

  stopAutoRefresh(): void {
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }
  }

  fetchOverviewData(isAutoRefresh: boolean = false): void {
    if (!isAutoRefresh) {
      this.isLoading = true;
    }
    const email = localStorage.getItem('email') || "";
    const viewType = this.currentMode.toLowerCase(); // "ndc" or "products"

    forkJoin({
      overview: this.overviewService.getOverviewData(email, viewType),
      analytics: this.overviewService.getAnalyticsData(email, viewType)
    }).subscribe({
      next: (res) => {
        // Handle Overview (KPI Cards)
        if (res.overview && res.overview.counts) {
          const c = res.overview.counts;
          this.activeCount = viewType === 'ndc' ? c.ndc : c.products;
          this.activeInShortagesCount = c.active_shortage || 0;
          this.discontinuedCount = c.approved_discontinuation || 0;
          this.resolvedCount = c.resolved || 0;
          this.newThisMonthCount = c.new_this_month || 0;
        }

        if (res.overview && res.overview.ids) {
          this.overviewIds = res.overview.ids;
        }

        // Handle Analytics (Charts)
        this.analyticsData = res.analytics;
        this.renderCharts();

        if (!isAutoRefresh) {
          this.isLoading = false;
        }
      },
      error: (err) => {
        console.error("Error fetching overview data", err);
        if (!isAutoRefresh) {
          this.isLoading = false;
        }
      }
    });
  }

  getFilteredData(): any[] {
    if (this.currentMode === "Products") {
      return this.allData.filter((d) => d.lms_flag === 1);
    }
    return this.allData;
  }

  getLatestExcel(d: any): any {
    if (!d.lms_entries || !d.lms_entries.length) return null;
    const entry = d.lms_entries[0];
    if (!entry.new_data || !entry.new_data.length) return null;
    return entry.new_data[0].excel_record;
  }

  renderCharts(): void {
    if (!this.analyticsData) return;

    Object.values(this.charts).forEach((c) => c.destroy());

    const { trend, status_distribution, categories, update_activity, route_distribution } = this.analyticsData;

    // Trend Chart
    const trendCtx = document.getElementById('monthlyChart') as HTMLCanvasElement;
    if (trendCtx && trend) {
      const latestTrend = (trend || []).slice(0, 12).reverse();
      this.charts.monthly = new Chart(trendCtx, {
        type: "line",
        data: {
          labels: latestTrend.map((t: any) => t.month),
          datasets: [{
            label: "Postings",
            data: latestTrend.map((t: any) => t.count),
            borderColor: "#9B4463",
            tension: 0.3,
            fill: true,
            backgroundColor: "rgba(155, 68, 99, 0.05)",
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: { boxWidth: 10, font: { size: 10 } }
            }
          },
          scales: {
            y: { grid: { display: false }, beginAtZero: true, ticks: { font: { size: 10 } } },
            x: { grid: { display: false }, ticks: { font: { size: 10 } } }
          }
        },
      });
    }

    // Status Chart
    const statusCtx = document.getElementById('statusChart') as HTMLCanvasElement;
    if (statusCtx && status_distribution) {
      this.charts.status = new Chart(statusCtx, {
        type: "doughnut",
        data: {
          labels: Object.keys(status_distribution || {}),
          datasets: [{
            data: Object.values(status_distribution || {}),
            backgroundColor: ["#E74C3C", "#F39C12", "#2ECC71", "#3498DB", "#9B59B6"],
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'right',
              labels: { boxWidth: 12, font: { size: 11 } }
            }
          },
          cutout: '65%'
        }
      });
    }

    // Category Chart
    if (categories && categories.length > 0) {
      const topCats = categories.slice(0, 14);
      const catCtx = document.getElementById('categoryChart') as HTMLCanvasElement;
      if (catCtx) {
        this.charts.category = new Chart(catCtx, {
          type: "bar",
          data: {
            labels: topCats.map((c: any) => c.category),
            datasets: [
              {
                label: "Current",
                data: topCats.map((c: any) => c.Current || 0),
                backgroundColor: "#9B4463",
              },
              {
                label: "To Be Discontinued",
                data: topCats.map((c: any) => c["To Be Discontinued"] || 0),
                backgroundColor: "#F39C12",
              },
            ],
          },
          options: {
            indexAxis: "y",
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: true, position: "top", labels: { boxWidth: 10, font: { size: 10 } } },
            },
            scales: {
              x: {
                stacked: false,
                grid: { display: false },
                ticks: { font: { size: 10 } }
              },
              y: {
                grid: { display: false },
                ticks: { font: { size: 7.5 } }
              }
            },
          },
        });
      }
    }

    // Activity Chart
    const actCtx = document.getElementById('activityChart') as HTMLCanvasElement;
    if (actCtx && update_activity) {
      this.charts.activity = new Chart(actCtx, {
        type: "polarArea",
        data: {
          labels: update_activity.map((a: any) => a.type),
          datasets: [{
            data: update_activity.map((a: any) => a.count),
            backgroundColor: ["#9B4463", "#F39C12", "#2ECC71", "#3498DB", "#9B59B6"],
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: true, position: "right", labels: { boxWidth: 10, font: { size: 10 } } }
          }
        }
      });
    }

    // Route Distribution Chart
    const routeCtx = document.getElementById('routeChart') as HTMLCanvasElement;
    if (routeCtx && Array.isArray(route_distribution) && route_distribution.length > 0) {
      const routeLabels = route_distribution.map((r: any) => r.route);
      const routeValues = route_distribution.map((r: any) => r.count);

      // Auto-size the canvas height so every bar has breathing room
      const barHeight = 28;
      const chartHeight = Math.max(300, routeLabels.length * barHeight + 40);
      routeCtx.parentElement!.style.height = `${chartHeight}px`;

      // Colour palette cycling through the brand palette
      const palette = [
        '#9B4463', '#3498DB', '#2ECC71', '#F39C12', '#9B59B6',
        '#E74C3C', '#1ABC9C', '#D35400', '#2980B9', '#8E44AD',
        '#27AE60', '#C0392B'
      ];
      const bgColors = routeLabels.map((_: any, i: number) => palette[i % palette.length]);

      this.charts.route = new Chart(routeCtx, {
        type: 'bar',
        data: {
          labels: routeLabels,
          datasets: [{
            label: 'NDCs by Route',
            data: routeValues,
            backgroundColor: bgColors,
            borderRadius: 4,
            borderSkipped: false,
          }]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => ` ${ctx.parsed.x.toLocaleString()} ${this.currentMode === "NDC" ? (ctx.parsed.x === 1 ? "NDC" : "NDCs") : (ctx.parsed.x === 1 ? "NDA" : "NDAs")}`
              }
            }
          },
          scales: {
            x: {
              grid: { display: false },
              beginAtZero: true,
              ticks: { font: { size: 10 } }
            },
            y: {
              grid: { display: false },
              ticks: { font: { size: 7.5 } }
            }
          }
        }
      });
    }
  }

  onFilterAndRedirect(type: string): void {
    let ids: string[] = [];
    if (this.overviewIds) {
      if (type === 'Active') {
        ids = this.currentMode === 'NDC' ? this.overviewIds.all_ndc_ids : this.overviewIds.products;
      } else if (type === 'Current') {
        ids = this.overviewIds.active_in_shortages || this.overviewIds.current || this.overviewIds.current_shortages || [];
      } else if (type === 'Discontinued') {
        ids = this.overviewIds.approved_discontinuation;
      } else if (type === 'Resolved') {
        ids = this.overviewIds.resolved;
      } else if (type === 'New') {
        ids = this.overviewIds.new_this_month;
      }
    }
    this.filterRedirect.emit({ type, ids });
  }
}
